import type { BlitzPage } from "@blitzjs/next"
import { Button, Stack, Typography, Link as MuiLink } from "@mui/material"
import { FirebaseError } from "firebase/app"
import { getAuth, signInWithEmailAndPassword } from "firebase/auth"
import Link from "next/link"
import { useRouter } from "next/router"
import { useSnackbar } from "notistack"
import { HomeAppBar } from "app/components/HomeAppBar"
import { LoginLayoutBox } from "app/register/components/CenterBox"
import { HomeRegisterForm } from "app/register/components/RegisterForm"
import {
  extractAuthProviderCode,
  toFirebaseAuthMessage,
} from "app/register/registerErrorMapping"
import { useCreateUserWithEmailMutation } from "interface/__generated__/react"
import type { OnSignUp } from "interface/types/onSignUp"
import { sendVerificationEmail } from "lib/auth/sendVerificationEmail"
import { t } from "lib/i18n";
import { getFromLocalStorage } from "util/localStorageUtil"

const RegisterPage: BlitzPage = () => {
  const [createUserMutation, { loading: isLoading }] =
    useCreateUserWithEmailMutation()

  const router = useRouter()

  const appURL = process.env.NEXT_PUBLIC_APP_URL

  // appURL 未設定なら continue URL を付けない（不正な "undefined/..." を避ける）
  const actionCodeSettings = appURL
    ? { url: `${appURL}/actions/auth/verification` }
    : undefined

  const { enqueueSnackbar } = useSnackbar()

  const onSignUp: OnSignUp = async (form) => {
    try {
      const createUserResult = await createUserMutation({
        variables: {
          input: {
            email: form.email,
            password: form.password,
          },
        },
      })
      const result = await signInWithEmailAndPassword(
        getAuth(),
        form.email,
        form.password,
      )
      // continue URL のドメインが Firebase 未承認でも確認メールが届くよう堅牢化（#741）。
      // 失敗しても登録は成立しているので await しつつ throw させない
      await sendVerificationEmail(result.user, actionCodeSettings)
      enqueueSnackbar(t("Verify your email"), { variant: "success" })

      // 招待がある場合はルートに移動する
      const inviteRequestId = getFromLocalStorage("inviteRequestId")

      if (inviteRequestId) {
        router.push("/")
        return
      } else {
        router.push("/businesses/new")
        return
      }
    } catch (error) {
      // クライアント側で起きた Firebase Authentication エラー
      if (error instanceof FirebaseError) {
        enqueueSnackbar(toFirebaseAuthMessage(error.code, error.message), {
          variant: "error",
        })
        return
      }

      // GraphQL 経由で返ってきたサーバー側 Firebase Authentication エラー
      const providerCode = extractAuthProviderCode(error)
      if (providerCode !== null) {
        enqueueSnackbar(toFirebaseAuthMessage(providerCode, ""), {
          variant: "error",
        })
        return
      }

      // その他の GraphQL / 一般エラー
      if (error instanceof Error) {
        const errorMessage = error.message
        if (errorMessage.includes("internal error")) {
          enqueueSnackbar(
            t("An error occurred during registration. Please try again."),
            { variant: "error" },
          )
        } else {
          enqueueSnackbar(errorMessage, { variant: "error" })
        }
      }
    }
  }

  return (
    <>
      <HomeAppBar>
        <Link href={"/"}>
          <Button>{t("Sign in")}</Button>
        </Link>
      </HomeAppBar>
      <LoginLayoutBox>
        <Typography
          textAlign={"center"}
          sx={{ fontSize: 24, fontWeight: "bold" }}
        >
          {t("Sign Up")}
        </Typography>
        <Stack spacing={1}>
          <HomeRegisterForm isLoading={isLoading} onSignUp={onSignUp} />
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <Typography>{t("Already have an account?")}</Typography>
            <Link href={"/"}>
              <MuiLink>{t("Sign in")}</MuiLink>
            </Link>
          </Stack>
        </Stack>
      </LoginLayoutBox>
    </>
  )
}

RegisterPage.getLayout = (page) => {
  return <>{page}</>
}

export default RegisterPage
